import $ from '../utils/net';
import OriginalConfig from './config';
import User from "./user";

// Riesponi la costante tipizzata
const Config = OriginalConfig;

/**
 * Interfacce per la tipizzazione dei parametri e dei ritorni
 */
export interface Country {
    code: string;
    name: string;
}

export interface ManagerBetListParams {
    lowlimit?: number;
    searchRecordPerPage?: number | null;
    orderby?: string | null;
    ordertype?: string | null;
    searchHelper?: any;
    searchStartDate?: any;
    searchEndDate?: any;
    useTicketEnd?: any;
    searchStatus?: any;
    searchSystemHide?: any;
    searchType?: any;
    searchExact?: any;
    searchString?: string | null;
    searchBetType?: any;
    searchBetEvents?: any;
    searchStock?: any;
    searchForPrese?: any;
    searchOutcome?: any;
}

export default class Admin {
    static setOperatorId(operatorId: number | null): void {
        Config.AccountApiClass!.WAdminAPI().setOperatorId(operatorId);
    }

    static async fetchCountries(): Promise<Country[]> {
        const countries: Country[] = [];

        const url = Config.softionAPI + '/admin/getCountries/?json' + 
                    (Config.publickey ? '&public=' + Config.publickey : '');

        await $.getJSON(url, (err: any, data: any) => {
            if (err) throw err;
            if (data && data.error) {
                throw data.error;
            }

            if (data && data.countries) {
                Object.entries(data.countries).forEach(([code, name]) => {
                    countries.push({ code, name: name as string });
                });
            }
        });

        return countries;
    }

    static async createUser(
        username: string,
        password: string,
        confirmPassword: string,
        email: string | null,
        phone: string | null,
        others: any
    ): Promise<any> {
        return await Config.AccountApiClass.registerStructUnder(
            User.data && User.data.id,
            'player',
            username,
            password,
            confirmPassword,
            email,
            phone,
            others
        );
    }

    static async userTransfer(
        idStrutturaOrigin: number | string,
        idCredenzialeTarget: number | string,
        idStrutturaTarget: number | string,
        amount: number,
        currency: string,
        notes: string = '',
        fido: boolean = false
    ): Promise<any> {
        return await Config.AccountApiClass.Accounting.performTransactions(
            idStrutturaOrigin, idCredenzialeTarget, idStrutturaTarget, amount, currency, notes, fido
        );
    }

    static async usernameUserTransfer(
        idStrutturaOrigin: number | string,
        usernameTarget: string,
        amount: number,
        currency: string,
        notes: string = '',
        fido: boolean = false
    ): Promise<any> {
        return await Config.AccountApiClass.Accounting.performTransactionToUsername(
            idStrutturaOrigin, usernameTarget, amount, currency, notes, fido
        );
    }

    static async userTransactions({
        idplayer = null,
        username = null,
        fromDate = null,
        toDate = null,
        page = 1,
        perPage = 30,
        isManager = false
    }: {
        idplayer?: any,
        username?: string | null,
        fromDate?: any,
        toDate?: any,
        page?: number,
        perPage?: number,
        isManager?: boolean
    }): Promise<any> {
        const start = fromDate ? new Date(fromDate) : new Date();
        const end = toDate ? new Date(toDate) : new Date();

        if (isManager) {
            return await Config.AccountApiClass.Accounting.getSubAccountTransactions(
                idplayer, { username }, start, end, page, perPage, true
            );
        } else {
            return await Config.AccountApiClass.Accounting.getTransactions(
                start, end, { username }, page, perPage, true
            );
        }
    }

    static async managerTransactions({
        username = null,
        fromDate = null,
        toDate = null,
        page = 1,
        perPage = 30
    }: {
        username?: string | null,
        fromDate?: any,
        toDate?: any,
        page?: number,
        perPage?: number
    }): Promise<any> {
        return await Config.AccountApiClass.Accounting.getMovimentiRete(
            null, null, fromDate, toDate, page, perPage, { username }, true
        );
    }

    static async managerListPlayers({
        lowlimit = 0,
        searchRecordPerPage = 10,
        idstructure = 0,
        idplayer = null,
        username = null
    }: {
        lowlimit?: number | string,
        searchRecordPerPage?: number | string,
        idstructure?: number | string,
        idplayer?: any,
        username?: string | null
    }): Promise<any> {
        const limit = parseInt(String(searchRecordPerPage));
        const offset = parseInt(String(lowlimit));
        
        // Calcolo della pagina (1-based) per il backend
        const page = (offset + limit) / limit;

        const userlist = await Config.AccountApiClass.Admin.getListPlayers(
            idplayer !== null ? idplayer : idstructure,
            idplayer !== null ? idplayer : username,
            page,
            limit,
            true
        );

        // Normalizzazione dati: fallback per email mancanti
        if (userlist && userlist.data) {
            userlist.data = userlist.data.map((item: any) => ({
                ...item,
                email: item.email || '-'
            }));
        }

        return userlist;
    }

    static async managerBetList(params: ManagerBetListParams): Promise<any> {
        return await Config.AccountApiClass.WAdminAPI().callBettingTickets(
            params.lowlimit ?? 0,
            params.searchRecordPerPage,
            params.orderby,
            params.ordertype,
            params.searchHelper,
            params.searchStartDate,
            params.searchEndDate,
            params.useTicketEnd,
            params.searchStatus,
            params.searchSystemHide,
            params.searchType,
            params.searchExact,
            params.searchString,
            params.searchBetType,
            params.searchBetEvents,
            params.searchStock,
            params.searchForPrese,
            params.searchOutcome
        ).ready();
    }

    static async managerReport({
        searchHelper = null,
        searchStartDate = null,
        searchEndDate = null,
    }: {
        searchHelper?: any,
        searchStartDate?: any,
        searchEndDate?: any,
    }): Promise<any> {
        return await Config.AccountApiClass.WAdminAPI().callSubfinancialMainFinancial(
            searchHelper,
            searchStartDate,
            searchEndDate
        ).ready();
    }

    static async managerChildren(idParent: number | string): Promise<any> {
        return await Config.AccountApiClass.Admin.getChildren(idParent);
    }

    static async managerStructure(idParent: number | string): Promise<any> {
        if ("getListAffiliates" in Config.AccountApiClass.Admin)
            return await Config.AccountApiClass.Admin.getListAffiliates(idParent);
        throw new Error("getListAffiliates unsupported");
    }

    static async ticketCancel(ticketId: number | string): Promise<any> {
        const WadminApi = Config.AccountApiClass.WAdminAPI();
        if ("callTicketCancel" in WadminApi)
            return await WadminApi.callTicketCancel(ticketId).ready();
        throw new Error("callTicketCancel unsupported");
    }
}